<script lang="ts">
    import { SpritePlayer } from "@/components";

    // All available sprite sheets (numbered PNGs in /assets/img/niji/animate/)
    const SPRITE_IDS = [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
        41,
    ];

    function spriteSrc(id: number): string {
        return `/assets/img/niji/animate/${id}_528.png`;
    }

    // -- Controls --
    let size = $state(48);
    let fps = $state(8);
    let frames = $state(8);
    let cropY = $state(0.23);
    let playing = $state(true);
    let pixelated = $state(false);
    let mode = $state<"action" | "uniform" | "idle">("action");

    const MODES: {
        value: "action" | "uniform" | "idle";
        label: string;
        desc: string;
    }[] = [
        {
            value: "action",
            label: "Action",
            desc: "Pixar timing + squash/stretch",
        },
        {
            value: "uniform",
            label: "Uniform",
            desc: "Equal frame timing, no transforms",
        },
        {
            value: "idle",
            label: "Idle",
            desc: "Equal frames + gentle breathing",
        },
    ];
</script>

<section class="st">
    <header class="st__header">
        <h1>Niji Sprite Test</h1>
        <p>
            {SPRITE_IDS.length} sprite sheets &middot; {frames} frames &middot; {fps}
            fps &middot; <strong>{mode}</strong> mode
        </p>
    </header>

    <div class="st__controls">
        <label class="st__control">
            <span>Size</span>
            <input type="range" min="48" max="256" step="8" bind:value={size} />
            <strong>{size}px</strong>
        </label>

        <label class="st__control">
            <span>FPS</span>
            <input type="range" min="1" max="60" step="1" bind:value={fps} />
            <strong>{fps}</strong>
        </label>

        <label class="st__control">
            <span>Frames</span>
            <input type="range" min="2" max="16" step="1" bind:value={frames} />
            <strong>{frames}</strong>
        </label>

        <label class="st__control">
            <span>Crop Y</span>
            <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                bind:value={cropY}
            />
            <strong>{Math.round(cropY * 100)}%</strong>
        </label>

        <div class="st__modeGroup">
            {#each MODES as m (m.value)}
                <button
                    class="st__mode"
                    class:st__mode--active={mode === m.value}
                    type="button"
                    title={m.desc}
                    onclick={() => (mode = m.value)}
                >
                    {m.label}
                </button>
            {/each}
        </div>

        <button
            class="st__toggle"
            class:st__toggle--active={playing}
            type="button"
            onclick={() => (playing = !playing)}
        >
            {playing ? "Pause" : "Play"}
        </button>

        <button
            class="st__toggle"
            class:st__toggle--active={pixelated}
            type="button"
            onclick={() => (pixelated = !pixelated)}
        >
            {pixelated ? "Pixelated" : "Smooth"}
        </button>
    </div>

    <div class="st__grid">
        {#each SPRITE_IDS as id (id)}
            <div class="st__cell">
                <SpritePlayer
                    src={spriteSrc(id)}
                    {size}
                    {frames}
                    {fps}
                    {playing}
                    {pixelated}
                    {cropY}
                    {mode}
                />
                <span class="st__id">#{id}</span>
            </div>
        {/each}
    </div>
</section>

<style lang="scss">
    .st {
        min-height: 100vh;
        background: var(--backgroundMain);
        color: var(--textColor);
        padding: 24px 16px;
        display: grid;
        gap: 20px;
        align-content: start;
        font-family: "Montserrat", sans-serif;
    }

    .st__header {
        text-align: center;
    }

    .st__header h1 {
        margin: 0;
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
    }

    .st__header p {
        margin: 4px 0 0;
        font-size: 13px;
        color: var(--subtleText);
    }

    .st__controls {
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        padding: 16px;
        border: 1px solid var(--border);
        border-radius: 16px;
        background: var(--white);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .st__control {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: var(--textColor);
        white-space: nowrap;
    }

    .st__control span {
        font-weight: 700;
        min-width: 48px;
    }

    .st__control input[type="range"] {
        width: 100px;
        accent-color: var(--primary);
    }

    .st__control strong {
        min-width: 36px;
        text-align: right;
        font-variant-numeric: tabular-nums;
    }

    .st__toggle {
        padding: 8px 16px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--backgroundMain);
        color: var(--textColor);
        font-size: 13px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        transition:
            background 0.15s,
            border-color 0.15s,
            color 0.15s;
    }

    .st__toggle--active {
        background: var(--primary);
        border-color: var(--primary);
        color: #12121f;
    }

    .st__modeGroup {
        display: flex;
        border: 1px solid var(--border);
        border-radius: 10px;
        overflow: hidden;
    }

    .st__mode {
        padding: 8px 14px;
        border: none;
        border-right: 1px solid var(--border);
        background: var(--backgroundMain);
        color: var(--textColor);
        font-size: 12px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        transition:
            background 0.15s,
            color 0.15s;
    }

    .st__mode:last-child {
        border-right: none;
    }

    .st__mode--active {
        background: var(--primary);
        color: #12121f;
    }

    .st__grid {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        justify-content: center;
    }

    .st__cell {
        display: grid;
        gap: 6px;
        justify-items: center;
    }

    .st__id {
        font-size: 11px;
        font-weight: 800;
        color: var(--subtleText);
        font-variant-numeric: tabular-nums;
    }
</style>
